import { useContext } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

import type { MaskitoOptions } from '@maskito/core';
import { useMaskito } from '@maskito/react';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { ReadOnlyField } from '@/lib/pages/forms/components/ReadOnlyField';
import { withMaskitoField } from '@/lib/utils/with-maskito-register';

// Formats phone numbers as '(555)-555-5555'
const phoneMask: MaskitoOptions = {
  mask: [
    '(',
    /\d/,
    /\d/,
    /\d/,
    ')',
    '-',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
  ],
};
function CustomPhoneInput<T extends FieldValues>({
  control,
  // options,
  name,
  label,
  placeholder,
  orientation = 'horizontal',
  type = 'text',
  required = false,
  overwriteLabelWidth,
  overwriteInputWidth,
  wideLabel,
  stretch = false,
  nativeRequired = false,
}: {
  control: Control<T>;
  name: Path<T> | string;
  label: string;
  placeholder?: string;
  orientation?: 'horizontal' | 'vertical';
  type?: string;
  required?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
  wideLabel?: boolean;
  stretch?: boolean;
  nativeRequired?: boolean;
}) {
  const { responsive, editMode } = useContext(AgentAuthorityContext);
  const maskitoRef = useMaskito({ options: phoneMask });

  const handleInvalid: React.FormEventHandler<HTMLInputElement> = (e) => {
    (e.target as HTMLInputElement).setCustomValidity(
      'Please enter the phone number in the format (555)-555-5555.'
    );
  };

  const handleInput: React.FormEventHandler<HTMLInputElement> = (e) => {
    (e.target as HTMLInputElement).setCustomValidity('');
  };

  return (
    <FormField
      control={control}
      name={name as Path<T>}
      render={({ field }) => {
        if (!editMode) {
          return <ReadOnlyField label={label} value={field.value} />;
        } else {
          const maskitoProps = withMaskitoField(field, maskitoRef);

          return (
            <FormItem
              className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'} ${responsive ? 'max-md:w-full max-md:flex-col' : ''}`}
            >
              <FormLabel
                className={`${orientation === 'horizontal' ? `${overwriteLabelWidth ? overwriteLabelWidth : `${stretch ? `${wideLabel ? 'w-2/3' : 'w-1/3'}` : `${wideLabel ? 'w-2/5' : 'w-1/5'}`}`}` : 'mb-2'} ${responsive ? 'max-md:w-full' : ''} font-semibold leading-[1.5rem]`}
              >
                {label}
                {required || nativeRequired ? (
                  <span className="text-red-400">*</span>
                ) : null}
              </FormLabel>
              <div
                className={`${orientation === 'horizontal' ? `${overwriteInputWidth ? overwriteInputWidth : `${stretch ? `${wideLabel ? 'w-1/3' : 'w-2/3'}` : `${wideLabel ? 'w-1/5' : 'w-2/5'}`}`}` : ''} ${responsive ? 'max-md:w-full' : ''} !mt-0`}
              >
                <FormControl>
                  <Input
                    // defaultValue={field.value}
                    required={nativeRequired}
                    type={type}
                    placeholder={placeholder}
                    onInvalid={handleInvalid}
                    pattern="^\([2-9][\d]{2}\)-[0-9]{3}-[0-9]{4}$"
                    {...maskitoProps}
                    onInput={(e) => {
                      handleInput(e);
                      maskitoProps.onChange(e);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          );
        }
      }}
    />
  );
}

export { CustomPhoneInput as PhoneInput };
